import type { i18n as I18n } from 'i18next'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Display name of a billing event type (`e-invoice:de:xrechnung:parse`, `pdf:de:generate`, …) — ONE way for every
 * usage chart in the console (dashboard ring + bars, billing „Usage" tab), so the same series has the same name on
 * both pages.
 *
 * Order:
 * 1. an explicit name in `common` under `dashboard.eventTypes.<id with _>` (`:` is i18next's namespace separator);
 * 2. composed from the id's structure — `e-invoice:<country>:<format>:<action>` and `pdf:<country>:<action>` →
 *    „XRechnung DE · Erstellung". The format name comes from the core `usage` bundle (`eventTypes.<format>`), the
 *    action word from `common` (`usageEventType.action.<action>`); an unknown action composes nothing;
 * 3. `extra[id]` — names the caller already has (billing: the `usage` bundle's exact ids and the quota catalog);
 * 4. the raw id — only when nothing above names it.
 */
export function eventTypeLabel(
  eventType: string,
  i18n: I18n,
  extra: Record<string, string> = {}
): string {
  const explicitKey = `dashboard.eventTypes.${eventType.replaceAll(':', '_')}`
  if (i18n.exists(explicitKey)) return i18n.t(explicitKey)

  const composed = composeLabel(eventType, i18n)
  if (composed) return composed

  return extra[eventType] ?? eventType
}

function composeLabel(eventType: string, i18n: I18n): string | null {
  const parts = eventType.split(':')
  if (parts.includes('*')) return null
  let country: string | undefined
  let format: string | undefined
  let action: string | undefined
  if (parts[0] === 'e-invoice' && parts.length === 4) {
    ;[, country, format, action] = parts
  } else if (parts[0] === 'pdf' && parts.length === 3) {
    ;[, country, action] = parts
    format = 'pdf'
  }
  if (!country || !format || !action) return null

  const actionKey = `usageEventType.action.${action}`
  if (!i18n.exists(actionKey)) return null
  const formatKey = `eventTypes.${format}`
  const formatName = i18n.exists(formatKey, { ns: 'usage' })
    ? i18n.t(formatKey, { ns: 'usage' })
    : format === 'pdf'
      ? 'PDF'
      : format

  return i18n.t('usageEventType.label', {
    format: formatName,
    country: country.toUpperCase(),
    action: i18n.t(actionKey),
  })
}

/** Hook form of {@link eventTypeLabel}; re-binds when the language changes. */
export function useEventTypeLabel(extra?: Record<string, string>): (eventType: string) => string {
  const { i18n } = useTranslation()
  const language = i18n.language
  return useCallback(
    (eventType: string) => {
      void language
      return eventTypeLabel(eventType, i18n, extra)
    },
    [i18n, language, extra]
  )
}
